<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8 ">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.edit-regular"></svg-vue>
          <h1 class="content-title">
            {{ $t('Edit repairable device') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <button class="btn btn-red rounded-none rounded-l-md" type="button" @click="deleteDeviceModal = true">
            {{ $t('Delete') }}
          </button>
          <router-link class="btn btn-app rounded-none rounded-r-md" to="/admin/repairable-devices">
            {{ $t('Back') }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8 ">
      <div class="content-card-body">
        <loading :status="loading"></loading>
        <form @submit.prevent="updateRepairableDevice">
          <div class="md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
            <div class="md:col-span-1">
              <h3 class="form-description-head">
                {{ $t('Repairable device information') }}
              </h3>
              <p class="form-description-text">{{ $t('Update repairable device information and services') }}.</p>
            </div>
            <div class="mt-5 md:mt-0 md:col-span-2">
              <div class="grid grid-cols-3 gap-6">
                <div class="col-span-3">
                  <label class="form-label" for="brand">{{ $t('Brand') }}</label>
                  <div class="pt-1">
                    <select class="form-input bg-white" id="brand" required v-model="device.brand_id">
                      <option :value="null" disabled>
                        {{ $t('Select an option') }}
                      </option>
                      <option :key="index" :value="brand.id" v-for="(brand, index) in brandList">
                        {{ brand.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="name">{{ $t('Name') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Name')" class="form-input" id="name" required v-model="device.name" />
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="model">{{ $t('Model') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Model')" class="form-input" id="model" required v-model="device.model" />
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="avator">{{ $t('Image') }}</label>
                  <image-input :remove="false" id="avator" v-model="image"></image-input>
                </div>
              </div>
            </div>
            <div class="md:col-span-3">
              <div class="py-3">
                <div class="border-t border-gray-200"></div>
              </div>
            </div>
            <div class="md:col-span-1">
              <h3 class="form-description-head">
                {{ $t('Repairable Services') }}
              </h3>
              <p class="form-description-text">{{ $t('Manage repairable service for the device') }}.</p>
            </div>
            <div class="mt-5 md:mt-0 md:col-span-2">
              <div class="grid grid-cols-3 gap-6">
                <div class="col-span-3">
                  <template v-if="defects.length > 0">
                    <ul>
                      <template v-for="(defect, index) in defects">
                        <li :class="{ 'border-t border-gray-200': index !== 0 }" :key="index">
                          <router-link
                            :to="`/admin/repairable-defects/${defect.id}/edit`"
                            class="
                              block
                              hover:bg-gray-100
                              focus:outline-none
                              focus:bg-gray-100
                              transition
                              duration-150
                              ease-in-out
                            "
                          >
                            <div class="flex items-center px-4 py-4 sm:px-6">
                              <div class="min-w-0 flex-1 flex items-center">
                                <div
                                  class="
                                    min-w-0
                                    flex-1
                                    pr-4
                                    lg:grid lg:grid-cols-2
                                    lg:gap-4
                                  "
                                >
                                  <div class="flex">
                                    <div
                                      class="
                                        inline-flex
                                        items-center
                                        px-2
                                        py-0.5
                                        rounded
                                        text-xs
                                        font-medium
                                        leading-4
                                        bg-blue-200
                                        text-blue-800
                                        mr-6
                                      "
                                    >
                                      {{ index + 1 }}
                                    </div>
                                    <div
                                      class="
                                        inline-block
                                        align-middle
                                        text-sm
                                        font-medium
                                        leading-5
                                      "
                                    >
                                      {{ defect.title }}
                                      <br />
                                      <span class="text-sm text-gray-800">
                                        {{ $t('Cost') }}: {{ defect.cost | currency }}, {{ $t('Price') }}: {{ defect.price | currency }},
                                        {{ $t('Time') }}:
                                        {{ defect.required_time }}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <svg-vue class="h-5 w-5" icon="font-awesome.angle-right-regular"></svg-vue>
                              </div>
                            </div>
                          </router-link>
                        </li>
                      </template>
                    </ul>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <div class="inline-flex">
              <router-link class="btn btn-secondary shadow-sm rounded-md mr-4" to="/admin/repairable-devices">{{ $t('Close') }}</router-link>
              <button class="btn btn-green shadow-sm rounded-md" type="submit">
                {{ $t('Update') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8 ">
      <div class="content-card-body">
        <loading :status="importing"></loading>
        <div class="md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
          <div class="md:col-span-1">
            <h3 class="form-description-head">
              {{ $t('Import repairable defects for device with CSV file') }}
            </h3>
            <p class="form-description-text">
              {{ $t('Upload file for batch entries') }}.
              <br />
              {{ $t('A file should be CSV with format') }}.
              <br />
              {{ $t('A file must have only columns with title of') }}
              <span class="text-red-600">"title","price","cost","time"</span>
            </p>
          </div>
          <div class="mt-5 md:mt-0 md:col-span-2">
            <div class="grid grid-cols-3 gap-6">
              <div class="col-span-3">
                <label class="form-label" for="name">{{ $t('Excel CSV file') }}</label>
                <div class="pt-1">
                  <input @change="onChangeFileUpload" class="form-input" ref="file" required type="file" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="card-footer">
          <div class="inline-flex">
            <button :disabled="!file" @click="importDeviceDefects" class="btn btn-app shadow-sm rounded-md" type="submit">
              {{ $t('Upload') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <device-delete-model :show="deleteDeviceModal" @cancelled="deleteDeviceModal = false" @confirmed="deleteRepairableDevice">
      <template #title>{{ $t('Delete repairable device') }}</template>
      {{ $t('Are you sure you want to delete the device?') }}
      {{ $t('All data will be permanently removed') }}.
    </device-delete-model>
  </main>
</template>
<script>
import confirmationModel from '@/components/layout/admin/confirmation';

export default {
  name: 'edit',
  components: {
    DeviceDeleteModel: confirmationModel,
  },
  metaInfo() {
    return {
      title: this.$i18n.t('Edit repairable device'),
    };
  },
  data() {
    return {
      loading: false,
      importing: false,
      deleteDeviceModal: false,
      brandList: [],
      defects: [],
      image: { file: null, preview: null },
      device: {
        name: null,
        model: null,
        brand_id: null,
      },
      file: null,
    };
  },
  mounted() {
    this.getRepairableDevice();
  },
  methods: {
    importDeviceDefects() {
      this.importing = true;
      const formData = new FormData();
      formData.append('file', this.file);
      formData.append('device_id', this.$route.params.id);
      axios
        .post(route('defects.import'), formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((response) => {
          this.importing = false;
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.$router.push('/admin/repairable-devices');
        })
        .catch(() => {
          this.importing = false;
        });
    },
    onChangeFileUpload() {
      this.file = this.$refs.file.files[0];
    },
    updateRepairableDevice() {
      this.loading = true;
      const formData = new FormData();
      formData.append('_method', 'PUT');
      formData.append('name', this.device.name);
      formData.append('model', this.device.model);
      formData.append('brand_id', this.device.brand_id);
      if (this.image.file) {
        formData.append('avatar', this.image.file);
      }
      axios
        .post(route('devices.update', this.$route.params.id), formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((response) => {
          this.loading = false;
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getRepairableDevice() {
      this.loading = true;
      axios
        .get(route('devices.show', this.$route.params.id))
        .then((response) => {
          this.device = response.data.device;
          this.image.preview = response.data.device.avatar;
          this.defects = response.data.device.defects;
          this.brandList = response.data.brand_list;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    deleteRepairableDevice() {
      axios
        .delete(route('devices.destroy', this.$route.params.id))
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.$router.push('/admin/repairable-devices');
        })
        .catch(() => {
          this.deleteDeviceModal = false;
        });
    },
  },
};
</script>
